import React, { useContext, useMemo, Fragment } from 'react';
import { Tooltip, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { PatientChartContext } from '../../../contexts/patientChart';
import { PATIENT, MODAL_KEY } from '../../../constants/patientChart.constant';
import { getDateTime } from '../../../utils/time';
import { useTime } from '../../../hooks/useTime';
import PPButton from '../../subComponents/button';
import TwilioVideoCallUI from '../../subComponents/twilioComponent';
import AppContainer from '../../../container/appContainer';

const TwilioVideoCallUIWithProps = AppContainer(TwilioVideoCallUI);

const ButtonStyles = (theme) => ({
  inactiveButtonStyle: {
    boxShadow: 'none',
    minWidth: 120,
    height: 30,
    width: '100%',
    textTransform: 'none',
    fontSize: 16,
    lineHeight: '20px',
    padding: '0 8px',
    fontWeight: 'bold',
    backgroundColor: 'var(--light-orange)',
    border: '1px solid var(--dark-primary)',
    color: 'var(--dark-primary)',
    fontFamily: "Proxima Nova",
    '&:hover': {
      backgroundColor: 'var(--light-orange)',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: 'var(--light-orange)',
      boxShadow: 'none',
    },
    '&:focus': {
      backgroundColor: 'var(--light-orange)',
      boxShadow: 'none',
    },
    '&:disabled': {
      boxShadow: 'none',
    }
  },
  activeButtonStyle: {
    boxShadow: 'none',
    minWidth: 120,
    height: 30,
    width: '100%',
    textTransform: 'none',
    fontSize: 16,
    lineHeight: '20px',
    padding: '0 8px',
    fontWeight: 'bold',
    backgroundColor: 'var(--orange-v1)',
    border: '1px solid var(--orange-v1)',
    color: 'var(--white)',
    fontFamily: "Proxima Nova",
    '&:hover': {
      backgroundColor: 'var(--orange-v2)',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: 'var(--orange-v2)',
      boxShadow: 'none',
    },
    '&:focus': {
      backgroundColor: 'var(--orange-v2)',
      boxShadow: 'none',
    }
  },
  activeButtonStyleV2: {
    boxShadow: 'none',
    minWidth: 120,
    height: 30,
    width: '100%',
    textTransform: 'none',
    fontSize: 16,
    lineHeight: '20px',
    padding: '0 8px',
    fontWeight: 'bold',
    backgroundColor: 'var(--primary)',
    border: '1px solid var(--primary)',
    color: 'var(--white)',
    fontFamily: "Proxima Nova",
    '&:hover': {
      backgroundColor: 'var(--primary)',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: 'var(--primary)',
      boxShadow: 'none',
    },
    '&:focus': {
      backgroundColor: 'var(--primary)',
      boxShadow: 'none',
    }
  },
  startGutter: {
    marginLeft: 8
  },
  endGutter: {
    marginRight: 8
  }
});

const StartButton = ({
  id,
  reference,
  onClick: handleClick = () => {},
  label = '',
  color = 'primary',
  variant = 'contained',
  disabled = false,
  active = false,
  endIcon,
  classes,
  startGutter = false,
  endGutter = false
}) => {
  const ButtonStyleCollection = new Map([
    [true, classes.activeButtonStyleV2],
    [false, classes.inactiveButtonStyle],
  ]);
  return (
    <Button
      id={id}
      ref={reference}
      className={`${ButtonStyleCollection.get(!!active)} ${startGutter && classes.startGutter} ${endGutter && classes.endGutter}`}
      variant={variant}
      color={color}
      onClick={handleClick}
      disabled={disabled}
      endIcon={endIcon}
    >
      {label}
    </Button>
  )
};

const StyledStartButton = withStyles(ButtonStyles)(StartButton);

export const StartVisit = ({
  orderId = '',
  doctorId = 0,
  patientId = 0,
  patient = PATIENT(),
  prescriber = {},
  videoCallDetails = false,
  isVideoFocused = false,
  isVideoCallStarted = false,

  getTokenForVideoCall = async () => {},
  startVisit = async () => {},
  getNotesTabInfo = async () => {},
  endVisit = async () => {},
  setStartVisit = async () => {},
  logError = async () => {},
}) => {
  const {
    loading,
    setLoading,
  } = useContext(PatientChartContext);

  const currentTime = useTime({ interval: 1 });

  const isActive = useMemo(() => {
    if (!patient.StartDateTime_UTC) {
      return false;
    }

    const { jsDate: startTime } = getDateTime({ date_UTC: patient.StartDateTime_UTC });
    const { jsDate: endTime } = getDateTime({ date_UTC: patient.EndDateTime_UTC });

    let activeTime = false;
    const bufferedMilliseconds = 10 * 60 * 1000;

    const startBufferedTime = startTime.epochTime - bufferedMilliseconds;
    const endBufferedTime = endTime.epochTime;

    // if current time is in between the scheduled buffered time
    if (
      currentTime.epochTime >= startBufferedTime &&
      currentTime.epochTime <= endBufferedTime) {
        activeTime = true;
    }

    return activeTime;
  }, [currentTime, patient]);

  const visitInfo = {
    OrderId: orderId,
    DoctorId: doctorId,
    UserId: patientId,
    VisitType: 1,
    FirstName: patient.FirstName,
    LastName: patient.LastName,
  };

  const handleStart = () => {
    if (!isActive) { return false; }

    const _loading = new Set(loading);
    _loading.add(MODAL_KEY.TWILIO);
    setLoading(_loading);

    getTokenForVideoCall({ userId: doctorId });
    return true;
  };

  const endVisitAndStopLoading = async (data) => {
    try {
      var response = await endVisit(data);
    } catch (e) {
      // handle errors if needed
    }

    // hides the loading
    const _loading = new Set(loading);
    _loading.delete(MODAL_KEY.TWILIO);
    setLoading(_loading);

    return response;
  };

  const StartButtonCollection = new Map([
    ['true-true', (
      <div className='flex patient-chart-info-section-b-video-visit '>
        <PPButton label='Start Visit' variant='contained' loading={loading.has(MODAL_KEY.TWILIO)} onClick={handleStart} flexFill />
      </div>
    )],
    ['false-true', (
      <Tooltip title='The current time is not within the scheduled visit time window' placement='top' arrow>
        <div className='flex patient-chart-info-section-b-video-visit'>
          <StyledStartButton label='Start Visit' loading={loading.has(MODAL_KEY.TWILIO)} active={isActive} onClick={handleStart} />
        </div>
      </Tooltip>
    )],
    ['true-false', <Fragment />],
    ['false-false', <Fragment />],
  ]);

  const TwilioVideoCallUITemplate = new Map([
    [true, (
      <div className='flex justify-content-flex-end patient-chart-info-section-b-video-visit'>
        <TwilioVideoCallUIWithProps
          loading={false}
          LogError={logError}
          callDetailSent={videoCallDetails}
          endVisit={endVisitAndStopLoading}
          CallDetails={startVisit}
          isVideoFocused={isVideoFocused}
          callStarted={isVideoCallStarted}
          SetStartVisit={setStartVisit}
          selectedPrescriberInfo={prescriber}
          selectedCardData={visitInfo}
          version={2}
          getNotesTabInfo={getNotesTabInfo}
        />
      </div>
    )],
    [false, (
      <Fragment>{StartButtonCollection.get(`${isActive}-${!!patient.StartDateTime_UTC}`)}</Fragment>
    )]
  ])

  return (
    <Fragment>
      {TwilioVideoCallUITemplate.get(!!isVideoCallStarted)}
      <div className='flex m-b-6'></div>
    </Fragment>
  );
};

export default StartVisit;
